import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'react-bootstrap';
import api from '../../../utils/api';
import {
  CLUB_SEED_LOAD_ERROR,
  CLUB_SEED_READONLY,
  clubSharedPlanCountLabel,
  clubSharedPlanMeta,
  clubSharedPlanTitle,
} from '../clubSharedPlans';
import { ClubSharedPlanDetail } from './ClubSharedPlanDetail';
import './ClubSharedPlansPanel.css';

export function ClubSharedPlansPanel({
  clubId,
  clubName = '',
  variant = 'embed',
  initialOpenPlanId = null,
}) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(Boolean(clubId));
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (!clubId) return undefined;
    let cancelled = false;
    setOpenId(null);
    (async () => {
      setLoading(true);
      setError('');
      try {
        const resp = await api.get(`/clubs/${clubId}/shared-plans/`);
        if (cancelled) return;
        const list = Array.isArray(resp?.data) ? resp.data : [];
        setPlans(list);
      } catch (e) {
        if (!cancelled) {
          setPlans([]);
          setError(CLUB_SEED_LOAD_ERROR);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clubId]);

  useEffect(() => {
    if (initialOpenPlanId == null) return;
    const match = plans.find((plan) => Number(plan.id) === Number(initialOpenPlanId));
    if (!match) return;
    setOpenId(match.id);
    const el = document.querySelector(`[data-testid="club-shared-plan-${match.id}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [plans, initialOpenPlanId]);

  if (!clubId) return null;

  if (loading) {
    return (
      <div
        className={variant === 'board' ? 'club-seed-board club-seed-board--loading' : 'club-detail-seed-block'}
        data-testid="club-shared-plans-loading"
      >
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  if (error || plans.length === 0) return null;

  const rows = (
    <ul className={variant === 'board' ? 'club-seed-board__list' : 'club-seed-summary-list mb-0'}>
      {plans.map((plan) => {
        const open = openId === plan.id;
        return (
          <li
            key={plan.id}
            className={`${variant === 'board' ? 'club-seed-board__item' : 'club-seed-summary-list__item'}${open ? ' is-open' : ''}`}
            data-testid={`club-shared-plan-${plan.id}`}
          >
            <button
              type="button"
              className={variant === 'board' ? 'club-seed-board__toggle' : 'club-seed-summary-list__toggle'}
              data-testid={`club-shared-plan-toggle-${plan.id}`}
              aria-expanded={open}
              aria-controls={`club-shared-plan-detail-${plan.id}`}
              onClick={() => setOpenId(open ? null : plan.id)}
            >
              <span className={variant === 'board' ? 'club-seed-board__copy' : 'club-seed-summary-list__main'}>
                <span className={variant === 'board' ? 'club-seed-board__name' : 'club-seed-summary-list__name'}>
                  {clubSharedPlanTitle(plan)}
                </span>
                <span className={variant === 'board' ? 'club-seed-board__meta' : 'club-seed-summary-list__meta'}>
                  {clubSharedPlanMeta(plan)}
                </span>
              </span>
              <span className={variant === 'board' ? 'club-seed-board__chip' : 'club-seed-summary-list__chip'}>
                {CLUB_SEED_READONLY}
              </span>
              <span className="club-seed-chevron" aria-hidden="true">{open ? '▾' : '▸'}</span>
            </button>
            {open ? (
              <ClubSharedPlanDetail plan={plan} clubName={clubName} variant={variant} />
            ) : null}
          </li>
        );
      })}
    </ul>
  );

  if (variant === 'board') {
    return (
      <section className="club-seed-board" data-testid="club-shared-plans" aria-label="Shared planting plans">
        <header className="club-seed-board__header">
          <span className="club-seed-board__title">SEED</span>
          <span className="club-seed-board__count">{clubSharedPlanCountLabel(plans.length)}</span>
        </header>
        {rows}
      </section>
    );
  }

  return (
    <div className="club-detail-seed-block" data-testid="club-shared-plans">
      <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
        <div>
          <div className="club-detail-section__subhead mb-0">Seed</div>
          <p className="club-detail-seed-block__lead mb-0">
            {plans.length} plan{plans.length === 1 ? '' : 's'} shared
          </p>
        </div>
      </div>
      {rows}
    </div>
  );
}

ClubSharedPlansPanel.propTypes = {
  clubId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  clubName: PropTypes.string,
  variant: PropTypes.oneOf(['embed', 'board']),
  initialOpenPlanId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ClubSharedPlansPanel;
